import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Download, Mail, AtSign } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CHAPTERS = [
  {
    n: "01",
    lead: "LOGO",
    rest: "DESIGN",
    desc: "Identity marks and logo systems for brands across finance, hospitality, healthcare, education, media and more.",
  },
  {
    n: "02",
    lead: "PACKAGE",
    rest: "DESIGN",
    desc: "Pouches, jars and labels built to win the shelf — agri, tea, skincare, spices and snacks.",
  },
  {
    n: "03",
    lead: "SOCIAL MEDIA",
    rest: "CREATIVE",
    desc: "Campaigns and daily creative for clubs, hospitals, IVF centres, academies, real estate and retail.",
  },
  {
    n: "04",
    lead: "POSTER &",
    rest: "PRINT DESIGN",
    desc: "Event posters, calendars, menus and X-stand displays — print that pulls people in.",
  },
];

const STATS = [
  { value: "5+", label: "Years Experience" },
  { value: "100+", label: "Global Clients" },
  { value: "04", label: "Disciplines" },
];

const fullSrc = (src) => (src.startsWith("/api") ? `${BACKEND}${src}` : src);

export default function PortfolioPage() {
  usePageMeta({
    title: "Abhishek Vyas — Graphic Design Portfolio | BrandZenith",
    description:
      "Graphic design portfolio of Abhishek Vyas — logo design, package design, social media creative and poster design. Five years, 100+ global clients.",
    path: "/portfolio",
    noindex: true,
  });

  const [pieces, setPieces] = useState(null);
  const [chapters, setChapters] = useState(CHAPTERS);

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([
          axios.get(`${API}/portfolio`),
          axios.get(`${API}/portfolio/chapters`),
        ]);
        setPieces(p.data);
        if (c.data.length) {
          setChapters(
            c.data.map((d) => ({ n: d.id, lead: d.lead, rest: d.rest, desc: d.desc }))
          );
        }
      } catch {
        setPieces([]);
      }
    })();
  }, []);

  const chapterPieces = (n) => (pieces || []).filter((p) => p.chapter === n);

  return (
    <>
      <Navbar />
      <main data-testid="portfolio-page">
        <section className="relative pt-44 pb-20 overflow-hidden">
          <div className="absolute inset-0 grid-bg" aria-hidden="true" />
          <motion.div
            aria-hidden="true"
            className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[360px] rounded-full bg-[#6D00FF]/15 blur-[140px]"
            animate={{ opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6"
            >
              Abhishek Vyas — Portfolio
            </motion.p>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tighter leading-[1.0]">
              <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  Graphic Design
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                <motion.span
                  className="block text-gradient-purple"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
                >
                  Portfolio.
                </motion.span>
              </span>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              {STATS.map((s) => (
                <div
                  key={s.label}
                  data-testid={`portfolio-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="glass-card rounded-md px-6 py-4"
                >
                  <p className="font-display text-2xl font-extrabold tracking-tight text-gradient-purple">
                    {s.value}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#A6A6B0] mt-1">
                    {s.label}
                  </p>
                </div>
              ))}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <a
                href="/portfolio/abhishek-vyas-portfolio.pdf"
                download
                data-testid="portfolio-download-pdf"
                className="flex items-center gap-2.5 bg-[#6D00FF] hover:bg-[#8B2CFF] text-white text-xs font-bold uppercase tracking-[0.14em] px-7 py-3.5 rounded-md transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_32px_-8px_rgba(139,44,255,0.8)]"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
              <Link
                to="/contact"
                data-testid="portfolio-contact-cta"
                className="flex items-center gap-2.5 border border-white/15 hover:border-[#8B2CFF]/60 text-white/90 hover:text-[#E8B6FF] text-xs font-bold uppercase tracking-[0.14em] px-7 py-3.5 rounded-md transition-colors duration-300"
              >
                Work With Me <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </section>

        <section className="relative py-16 lg:py-20 border-t border-white/[0.05]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#8B2CFF] mb-6">
                About Me
              </p>
              <h2 className="font-display text-2xl sm:text-4xl font-extrabold uppercase tracking-tight leading-[1.1]">
                The designer behind <span className="text-gradient-purple">the craft.</span>
              </h2>
              <div className="mt-8 space-y-3">
                <a
                  href="mailto:thebrandcraftsman@gmail.com"
                  data-testid="portfolio-email-link"
                  className="flex items-center gap-3 text-sm text-[#A6A6B0] hover:text-white transition-colors duration-300"
                >
                  <span className="w-9 h-9 rounded-md border border-[#8B2CFF]/40 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-[#C43CFF]" strokeWidth={1.5} />
                  </span>
                  thebrandcraftsman@gmail.com
                </a>
                <p className="flex items-center gap-3 text-sm text-[#A6A6B0]">
                  <span className="w-9 h-9 rounded-md border border-[#8B2CFF]/40 flex items-center justify-center">
                    <AtSign className="w-4 h-4 text-[#C43CFF]" strokeWidth={1.5} />
                  </span>
                  thebrandcraftman
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-base sm:text-lg text-[#A6A6B0] leading-relaxed">
                With over five years of dedicated experience, I have honed my expertise in graphic
                design, social media management and website development. My skill set encompasses
                the art of crafting distinctive logos, creating visually appealing social media
                posts, executing the development of WordPress websites, establishing impactful
                branding strategies, and producing engaging video animations.
              </p>
              <p className="mt-6 text-base sm:text-lg text-[#A6A6B0] leading-relaxed">
                As a seasoned freelancer, I have successfully collaborated with and catered to the
                unique needs of over 100 clients on a global scale.
              </p>
            </Reveal>
          </div>
        </section>

        {pieces === null ? (
          <p className="text-center text-sm text-[#A6A6B0] py-20">Loading portfolio…</p>
        ) : (
          chapters.map((ch) => {
            const items = chapterPieces(ch.n);
            if (!items.length) return null;
            return (
              <section
                key={ch.n}
                data-testid={`portfolio-chapter-${ch.n}`}
                className="relative py-16 lg:py-24 border-t border-white/[0.05]"
              >
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                  <div className="flex flex-wrap items-end gap-6 sm:gap-10 mb-12 lg:mb-16">
                    <Reveal>
                      <span
                        aria-hidden="true"
                        className="font-display text-7xl sm:text-8xl font-extrabold tracking-tighter leading-none text-white/[0.07]"
                      >
                        {ch.n}
                      </span>
                    </Reveal>
                    <Reveal delay={0.1}>
                      <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[1.05]">
                        <span className="text-gradient-purple">{ch.lead}</span> {ch.rest}
                      </h2>
                      <p className="mt-3 text-sm sm:text-base text-[#A6A6B0] max-w-xl">{ch.desc}</p>
                    </Reveal>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
                    {items.map((piece, i) => (
                      <Reveal key={piece.id} delay={(i % 3) * 0.08} y={24}>
                        <div
                          data-testid={`portfolio-piece-${ch.n}-${i}`}
                          className="group relative overflow-hidden rounded-lg border border-white/[0.06] bg-[#0a0a10] transition-[border-color,box-shadow] duration-500 hover:border-[#6D00FF]/40 hover:shadow-[0_0_44px_-12px_rgba(109,0,255,0.5)]"
                        >
                          <img
                            src={fullSrc(piece.src)}
                            alt={piece.title || `${ch.lead} ${ch.rest} — portfolio piece ${i + 1} by Abhishek Vyas`}
                            loading="lazy"
                            className="w-full h-auto block transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03]"
                          />
                          {piece.title && (
                            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                              <p className="text-xs font-semibold text-white">{piece.title}</p>
                            </div>
                          )}
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              </section>
            );
          })
        )}

        <section className="relative py-28 lg:py-40 border-t border-white/[0.05] overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(95vw,800px)] h-[400px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(109,0,255,0.25) 0%, transparent 70%)",
            }}
          />
          <div className="relative max-w-3xl mx-auto px-6 text-center">
            <Reveal>
              <h2 className="font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tighter">
                Thank <span className="text-gradient-purple">you.</span>
              </h2>
              <p className="mt-8 text-base sm:text-lg text-[#A6A6B0] leading-relaxed">
                Thank you for exploring my graphic design portfolio. Each project represents my
                dedication to creativity and craftsmanship. I look forward to the possibility of
                collaborating on exciting projects together — let's bring your vision to life with
                captivating design solutions.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/contact"
                data-testid="portfolio-final-cta"
                className="mt-10 inline-flex items-center gap-3 bg-[#6D00FF] hover:bg-[#8B2CFF] text-white text-sm font-semibold uppercase tracking-[0.12em] px-9 py-4 rounded-md transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_44px_-8px_rgba(139,44,255,0.8)]"
              >
                Request a Proposal <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
